import { Button, FormLabel, Modal, TextField, Typography } from '@mui/material'
import { type ChangeEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import * as S from './styles'

interface Props {
	isModalOpened: boolean
	onClose: () => void
	onRejectEmissionRequest: (comment: string) => Promise<void>
}

const RejectEmissionModal: React.FC<Props> = ({
	isModalOpened,
	onClose,
	onRejectEmissionRequest,
}) => {
	const { t } = useTranslation()
	const [comment, setComment] = useState<string>('')
	const [isTryingToCommit, setIsTryingToCommit] = useState(false)

	const trimmedComment = comment.trim()

	const handleFormCommentChange = useCallback(
		(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setComment(event.target.value)
			setIsTryingToCommit(false)
		},
		[]
	)

	const cancelAddProduct = useCallback(() => {
		setIsTryingToCommit(false)
		setComment('')
		onClose()
	}, [onClose])

	const confirmButtonClicked = useCallback(async () => {
		if (!trimmedComment) {
			setIsTryingToCommit(true)
		} else {
			await onRejectEmissionRequest(trimmedComment)
			cancelAddProduct()
		}
	}, [trimmedComment, onRejectEmissionRequest, cancelAddProduct])

	return (
		<Modal open={isModalOpened} onClose={onClose}>
			<S.BackdropWithModalStyles>
				<S.Header>
					<Typography variant="h2">
						{t('modal.rejectEmissions.title')}
					</Typography>
				</S.Header>

				<S.ContentContainer>
					<FormLabel htmlFor="reject-comment-input">
						{t('modal.rejectEmissions.sectionTitle')}
					</FormLabel>
					<TextField
						error={isTryingToCommit && !trimmedComment}
						id="reject-comment-input"
						variant="filled"
						value={comment}
						onChange={handleFormCommentChange}
						multiline
						rows={4}
						sx={{ width: 600 }}
					/>
				</S.ContentContainer>

				<S.Footer>
					<Button variant="outlined" color="primary" onClick={cancelAddProduct}>
						{t('button.cancel')}
					</Button>
					<Button
						disabled={trimmedComment.length === 0}
						variant="contained"
						color="primary"
						onClick={confirmButtonClicked}
					>
						{t('button.confirm')}
					</Button>
				</S.Footer>
			</S.BackdropWithModalStyles>
		</Modal>
	)
}

export { RejectEmissionModal }
